import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import gql from "graphql-tag";
import { useSearchParams } from "react-router-dom";

import { useAuth } from "auth/AuthContext";
import { ExpiredLinkButton } from "auth/components/ExpiredLinkButton";
import { InvalidLinkError } from "auth/components/InvalidLinkError";
import { VerifyMfaForm } from "auth/components/VerifyMfaForm";
import { mapToKnownNablaRegionOrNull } from "auth/utils";
import {
  TARGET_FRONTEND_SEARCH_PARAM,
  useNavigatePreservingTargetParams,
} from "auth/utils/copilot";
import { buildVerifyMfaPayload } from "auth/utils/mfa";
import {
  PublicScreenImage,
  PublicScreenWrapperWithEnvironmentBanner,
} from "components/PublicScreenWrapper/PublicScreenWrapper";
import { Spinner } from "components/Spinner/Spinner";
import { AppErrorCode } from "errors/generated";
import {
  RedeemLoginCode,
  RegionForEmail,
  RequestLoginCode,
  TargetFrontendKnownValues,
} from "generated/unauth-account";
import { ParsedGraphQLError } from "graphql-client/errors";
import { useLazyQuery } from "graphql-client/useLazyQuery";
import { useMutation } from "graphql-client/useMutation";
import { getKnownValue } from "utils/enum";
import { notifier } from "utils/notifier";

import { routes } from "../../routes";

gql`
  # schema = UNAUTHENTICATED_ACCOUNT
  fragment LoginResponse on LoginResponse {
    ... on LoginResponseSuccess {
      jwtTokens {
        accessToken
        refreshToken
      }
    }
    ... on LoginResponseMfaRequired {
      mfaState {
        ... on SetupMfaState {
          supportedMethods {
            ... on TotpMfaMethod {
              isSetup
            }
          }
        }
      }
    }
  }

  mutation RequestLoginCode(
    $email: String!
    $targetFrontend: TargetFrontend!
    $includeMagicLink: Boolean
  ) {
    requestLoginCode(
      email: $email
      targetFrontend: $targetFrontend
      includeMagicLink: $includeMagicLink
    ) {
      _
    }
  }

  mutation RedeemLoginCode($email: String!, $code: String!, $mfaCode: String) {
    redeemLoginCode(email: $email, code: $code, mfaCode: $mfaCode) {
      loginResponse {
        ...LoginResponse
      }
    }
  }
`;

type LoginLinkState =
  | { type: "REDEEMING" }
  | { type: "REDEEMED" }
  | { type: "NEEDS_MFA" }
  | { type: "EXPIRED" }
  | { type: "ALREADY_USED" }
  | { type: "FAILED" };

type RedeemValueType = { jwt: string } | { code: string };

export type CodeInputMode = "MANUAL" | "LINK";

export const AccountLoginLink = () => {
  const { accountLoginWithTokens } = useAuth();
  const navigatePreservingTargetParams = useNavigatePreservingTargetParams();
  const [searchParams] = useSearchParams();
  const jwt = searchParams.get("oneTimeLoginJwt");
  const code = searchParams.get("accountLoginMagicLinkCode");
  const inputMode = searchParams.get("inputMode");
  const redeemParam: RedeemValueType | undefined = useMemo(
    () => (jwt ? { jwt } : code ? { code } : undefined),
    [code, jwt],
  );
  const email = searchParams.get("email");
  const targetFrontend = getKnownValue(
    searchParams.get(TARGET_FRONTEND_SEARCH_PARAM),
    TargetFrontendKnownValues,
  );
  const publicScreenImage: PublicScreenImage =
    targetFrontend === "COPILOT_WEB_APP" ||
    targetFrontend === "COPILOT_CHROME_EXTENSION"
      ? "COPILOT"
      : "CARE_PLATFORM";

  const shouldRedeemLink = !!email && !!targetFrontend && !!redeemParam;
  const startedRedeemingLinkRef = useRef(false);
  const [fetchRegionForEmail] = useLazyQuery(RegionForEmail);
  const [redeemLoginCode] = useMutation(RedeemLoginCode, {
    throwOnError: true,
  });
  const [requestLoginCode] = useMutation(RequestLoginCode);
  const [loginLinkState, setLoginLinkState] = useState<LoginLinkState>({
    type: "REDEEMING",
  });

  const redeemAndParse = useCallback(
    async (
      currentEmail: string,
      redeemValue: RedeemValueType,
      mfaCode?: string,
    ): Promise<LoginLinkState> => {
      try {
        let response;
        if ("code" in redeemValue) {
          const regionForEmailResult = await fetchRegionForEmail({
            variables: {
              email: currentEmail,
            },
          });
          if (!regionForEmailResult.data?.regionForEmail?.region) {
            throw new Error("No nabla region found.");
          }
          const requestRegion = mapToKnownNablaRegionOrNull(
            regionForEmailResult.data.regionForEmail.region,
          );
          if (!requestRegion) throw new Error("Nabla region not supported.");

          response = await redeemLoginCode(
            { email: currentEmail, code: redeemValue.code, mfaCode },
            { requestContext: { region: requestRegion } },
          );
        } else {
          throw new Error("Invalid redeem value");
        }
        const { loginResponse } = response;
        if (loginResponse.__typename === "LoginResponseSuccess") {
          accountLoginWithTokens({
            accountAccessToken: loginResponse.jwtTokens.accessToken,
            accountRefreshToken: loginResponse.jwtTokens.refreshToken,
          });
          navigatePreservingTargetParams(routes.PICK_IDENTITY, {
            replace: true,
            state: { shouldAutomaticallyPickSingleIdentity: true },
          });
          return { type: "REDEEMED" };
        }

        if (loginResponse.__typename === "LoginResponseMfaRequired") {
          const payload = buildVerifyMfaPayload(loginResponse.mfaState);
          return payload ? { type: "NEEDS_MFA" } : { type: "FAILED" };
        }

        return { type: "FAILED" };
      } catch (e) {
        if (e instanceof ParsedGraphQLError) {
          switch (e.code) {
            case AppErrorCode.EXPIRED_VERIFICATION_CODE:
              return { type: "EXPIRED" };
            case AppErrorCode.ALREADY_USED_VERIFICATION_CODE:
              return { type: "ALREADY_USED" };
          }
        }
        return { type: "FAILED" };
      }
    },
    [
      accountLoginWithTokens,
      navigatePreservingTargetParams,
      redeemLoginCode,
      fetchRegionForEmail,
    ],
  );

  useEffect(() => {
    if (!shouldRedeemLink) return;
    if (startedRedeemingLinkRef.current) return;
    startedRedeemingLinkRef.current = true;
    redeemAndParse(email, redeemParam).then(setLoginLinkState);
  }, [code, email, jwt, redeemAndParse, redeemParam, shouldRedeemLink]);

  return !shouldRedeemLink ? (
    <PublicScreenWrapperWithEnvironmentBanner image={publicScreenImage}>
      <InvalidLinkError />
    </PublicScreenWrapperWithEnvironmentBanner>
  ) : loginLinkState.type === "REDEEMING" ? (
    <Spinner />
  ) : loginLinkState.type === "EXPIRED" ? (
    <PublicScreenWrapperWithEnvironmentBanner image={publicScreenImage}>
      <ExpiredLinkButton
        email={email}
        onClick={async () => {
          try {
            const regionForEmailResult = await fetchRegionForEmail({
              variables: { email },
            });
            if (!regionForEmailResult.data?.regionForEmail?.region) {
              throw new Error("No nabla region found.");
            }
            const requestRegion = mapToKnownNablaRegionOrNull(
              regionForEmailResult.data.regionForEmail.region,
            );
            if (!requestRegion) throw new Error("Nabla region not supported.");

            const didRequest = await requestLoginCode(
              { email, targetFrontend, includeMagicLink: true },
              { requestContext: { region: requestRegion } },
            );
            return didRequest ? "SUCCESS" : "ERROR";
          } catch (e) {
            console.error(e);
            notifier.error({
              user: "Unexpected region query error",
              sentry: { exception: e },
            });
            return "ERROR";
          }
        }}
      />
    </PublicScreenWrapperWithEnvironmentBanner>
  ) : loginLinkState.type === "NEEDS_MFA" ? (
    <PublicScreenWrapperWithEnvironmentBanner image={publicScreenImage}>
      <VerifyMfaForm
        onSubmit={async (mfaCode) => {
          setLoginLinkState({ type: "REDEEMING" });
          const state = await redeemAndParse(email, redeemParam, mfaCode);
          setLoginLinkState(state);
          return state.type === "REDEEMED" ? "SUCCESS" : "ERROR";
        }}
      />
    </PublicScreenWrapperWithEnvironmentBanner>
  ) : (
    <PublicScreenWrapperWithEnvironmentBanner image={publicScreenImage}>
      <InvalidLinkError
        codeInputMode={inputMode === "MANUAL" ? "MANUAL" : "LINK"}
        reason={
          loginLinkState.type === "ALREADY_USED" ? "ALREADY_USED" : "INVALID"
        }
        targetFrontend={targetFrontend}
      />
    </PublicScreenWrapperWithEnvironmentBanner>
  );
};
